import { Component, forwardRef, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
} from '@angular/forms';
import { startCase } from 'lodash';

@Component({
  selector: 'rmp-text-area',
  templateUrl: './text-area.component.html',
  styleUrl: './text-area.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextAreaComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TextAreaComponent),
      multi: true,
    },
  ],
})
export class TextAreaComponent implements ControlValueAccessor {
  @Input() formGroup: FormGroup | undefined;
  @Input() formControlName: string = '';
  @Input() characterLimit: number = 200;
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() rows: number = 4;
  @Input() error: string | undefined;
  @Input() isRequired: boolean = true;
  @Input() alwaysShowIsRequired: boolean = false;
  value: string = '';
  onChange: any = () => {};
  onTouched: any = () => {};
  isControlValid: boolean = true;
  disabled: boolean = false;

  writeValue(value: any): void {
    if (value) {
      this.value = value;
      this.resetRequiredValidationError();
      this.isControlValid = !this.hasError();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  GetContentLength() {
    return this.formGroup?.controls[this.formControlName]?.value?.length ?? 0;
  }
  validate(control: AbstractControl): ValidationErrors | null | undefined {
    return this.formGroup?.controls[this.formControlName]?.errors;
  }
  onInput(event: any): void {
    const target = event.target as HTMLInputElement;
    this.value = target.value;
    if (this.value && this.value.trim().length > 0) {
      this.resetRequiredValidationError();
      if (this.value.length > this.characterLimit) {
        this.value = this.value.substring(0, this.characterLimit);
        this.formGroup?.controls[this.formControlName].setValue(this.value);
      }
      this.isControlValid = !this.hasError();
    }
    this.onChange(this.value);
    this.onTouched();
  }
  private resetRequiredValidationError() {
    const controlErrors = this.formGroup?.controls[this.formControlName].errors;
    if (controlErrors) {
      delete controlErrors['required'];
      this.formGroup?.controls[this.formControlName].setErrors(
        Object.keys(controlErrors).length ? controlErrors : null,
      );
    }
  }

  OnfocusOut($event: FocusEvent) {
    this.isControlValid = !this.hasError();
    this.onTouched();
  }
  GetLabel() {
    return this.label || startCase(this.formControlName);
  }
  GetPlaceholder() {
    return `Enter ${this.placeholder || this.label || startCase(this.formControlName)}`;
  }
  GetError(): string {
    const control = this.formGroup?.controls[this.formControlName];
    let errorMessage = '';
    for (const key in control?.errors) {
      if (control?.errors[key]) {
        errorMessage = this.getErrorMessage(key, control?.errors[key], control);
        break;
      }
    }
    return errorMessage;
  }
  getErrorMessage(
    validationErrorType: string,
    validationError: any,
    control: AbstractControl<any, any>,
  ): string {
    const label = this.GetLabel();
    switch (validationErrorType) {
      case 'required':
        return `${label} is required`;
      case 'minlength':
        return `${label} must be at least ${control.errors?.['minlength'].requiredLength} characters long`;
      case 'maxlength':
        return `${label} must be less than ${control.errors?.['maxlength'].requiredLength} characters long`;
      case 'pattern':
        return `${label} is not valid`;
      default:
        return `${label} is invalid`;
    }
  }
  hasError(): boolean {
    const control = this.formGroup?.controls[this.formControlName];
    const invalid = !!(control && control.touched && control.invalid);

    return invalid;
  }
  getMinHeight(): string {
    const lineHeight = 25; // Adjust this value based on your CSS
    return `${this.rows * lineHeight - this.rows}px`;
  }

  clear() {
    this.value = '';
    this.onChange(this.value);
    this.onTouched();
    this.isControlValid = !this.hasError();
  }
}
